import { useMutation, useQuery } from '@apollo/client'
import { useCallback, useState } from 'react'
import { toast } from 'sonner'
import { v4 as uuidv4 } from 'uuid'
import type { z } from 'zod'

import { CREATE_USER } from '../../../../../../data/mutations/user'
import { DOES_USER_EXIST } from '../../../../../../data/queries/members'
import type { IMembership, IUserExistsReturnData } from '../../../../../../types/user'
import { CreatedVia } from '../../../../../../types/user'
import { isEmail } from '../../../../../../utils/common'
import type { emailPhoneInputSchema } from '../../../shared/email-phone-form'
import type { IAssignDispatcherInput } from '../../types'

interface IDispatcherData {
  id: string
  isShipmentOwner: boolean
  name: string
  workspaceID: string
}

interface IDispatcherState {
  addedByWorkspaceID: string
  dispatcher: IDispatcherData
}

const INITIAL_DISPATCHER_STATE: IDispatcherData = {
  id: '',
  isShipmentOwner: false,
  name: '',
  workspaceID: ''
}

/**
 * Custom hook to handle dispatcher search and assignment in a shipment
 */
export const useDispatchersList = (
  handleAddDispatcher: (input: IAssignDispatcherInput) => Promise<void>,
  workspaceID: string
) => {
  const [userInput, setUserInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [dispatcherWorkspaceList, setDispatcherWorkspaceList] = useState<IMembership[]>([])
  const [newDispatcherData, setNewDispatcherData] = useState<IDispatcherState>({
    addedByWorkspaceID: workspaceID,
    dispatcher: INITIAL_DISPATCHER_STATE
  })

  const setLoadingWithToast = (message: string, isError = true) => {
    setLoading(false)
    isError ? toast.error(message) : toast.success(message)
  }

  const [createUser] = useMutation(CREATE_USER, {
    onCompleted: async () => {
      await handleAddDispatcher(newDispatcherData)
      setLoading(false)
    },
    onError: (error) => setLoadingWithToast(error.message)
  })

  const { refetch: userExists } = useQuery(DOES_USER_EXIST, {
    fetchPolicy: 'network-only',
    skip: true
  })

  const createDispatcherData = (emailOrPhone: string, id: string, name = ''): IDispatcherState => ({
    addedByWorkspaceID: workspaceID,
    dispatcher: {
      ...INITIAL_DISPATCHER_STATE,
      ...(isEmail(emailOrPhone) ? { email: emailOrPhone } : { countryCode: '+1', phoneNumber: emailOrPhone }),
      id,
      name: name || '',
      workspaceID: ''
    }
  })

  const handleAssignDispatcher = useCallback(
    async (selectedWorkspaceID: string, dispatcherDataOverride?: IDispatcherState) => {
      try {
        const isShipmentOwner = dispatcherWorkspaceList.some((workspace) => workspace.workspaceID === workspaceID)

        // Use override if provided (to avoid race condition with state updates), otherwise use state
        const baseData = dispatcherDataOverride || newDispatcherData

        const dispatcherData = {
          ...baseData,
          dispatcher: {
            ...baseData.dispatcher,
            isShipmentOwner,
            workspaceID: selectedWorkspaceID
          }
        }

        await handleAddDispatcher(dispatcherData)
        setDispatcherWorkspaceList([])
        setLoading(false)
      } catch (error) {
        setLoadingWithToast(error instanceof Error ? error.message : 'Failed to assign dispatcher')
      }
    },
    [newDispatcherData, dispatcherWorkspaceList, handleAddDispatcher, workspaceID]
  )

  const handleNewUser = async (emailOrPhone: string) => {
    const id = uuidv4()
    const dispatcherData = createDispatcherData(emailOrPhone, id)
    setNewDispatcherData({
      addedByWorkspaceID: workspaceID,
      dispatcher: {
        ...INITIAL_DISPATCHER_STATE,
        ...dispatcherData.dispatcher
      }
    })

    await createUser({
      variables: {
        createdVia: CreatedVia.ChaineWebApp,
        emailOrPhone,
        userID: id
      }
    })
  }

  const handleExistingUser = async (existingUser: IUserExistsReturnData, dispatcherData: IDispatcherState) => {
    try {
      const { memberships, id, name, email, phone } = existingUser

      if (memberships.length > 1) {
        setDispatcherWorkspaceList(memberships)
        return
      }

      if (memberships.length === 1) {
        // Pass dispatcherData directly to avoid race condition with state updates
        await handleAssignDispatcher(memberships[0].workspaceID, dispatcherData)
        return
      }

      await handleAddDispatcher(createDispatcherData(email || phone || '', id, name || ''))
    } catch (error) {
      setLoadingWithToast(error instanceof Error ? error.message : 'Failed to process existing user')
    }
  }

  const handleSubmit = useCallback(
    async (data: z.infer<typeof emailPhoneInputSchema>) => {
      const cleanedEmailPhone = data.cleanedEmailPhone?.trim()
      if (!cleanedEmailPhone) return

      setUserInput(cleanedEmailPhone)
      setLoading(true)

      try {
        const { data: response } = await userExists({ emailOrPhone: cleanedEmailPhone })
        if (!response) return

        const { userExists: existingUser } = response
        if (existingUser) {
          const dispatcherData = createDispatcherData(cleanedEmailPhone, existingUser.id, existingUser.name)
          setNewDispatcherData(dispatcherData)
          await handleExistingUser(existingUser, dispatcherData)
        } else {
          await handleNewUser(cleanedEmailPhone)
        }
      } catch (error) {
        setLoadingWithToast(error instanceof Error ? error.message : 'Failed to process request')
      }
    },
    [userExists]
  )

  const resetInviteState = () => {
    setLoading(false)
    setDispatcherWorkspaceList([])
  }

  return {
    dispatcherWorkspaceList,
    handleAssignDispatcher,
    handleSubmit,
    loading,
    resetInviteState,
    setUserInput,
    userInput
  }
}
